import {Avatar, Box, BoxProps, Tooltip, Typography, CircularProgress} from "@mui/material";
import {makeStyles} from "tss-react/mui";
import React, {useState, useEffect} from "react";
import {formatTimeSecondsApprox} from "@buildwitt/shapeshift-common/v1/common";
import NotificationsIcon from "@mui/icons-material/Notifications";
import CloseIcon from "@mui/icons-material/Close";
import {
	LearnerAssignedAnnouncementDto,
	useCompleteAnnouncement,
	useMarkNotificationReadById
} from "@/app2/shared/data";
import {NotificationItemDto, NotificationItemDtoNotificationType} from "@/app2/shared/data";
import {App2AnnouncementModal} from "@/app2/pages/HomePage/AnnouncementsSection/App2AnnouncementModal";
import {AnnouncementContentChips} from "@/app2/pages/HomePage/AnnouncementsSection/AnnouncementContentChips";

const useStyles = makeStyles<NotificationItemDto>()((theme, notification) => ({
	historicNotificationItemAnnouncement: {
		backgroundColor:
			(notification?.notificationType !== "announcement" && notification.read) ||
			(notification?.notificationType === "announcement" && notification?.complete)
				? "#333333" + " !important"
				: "rgba(60, 56, 49)" + " !important",
		padding: "16px",
		borderRadius: "16px",
		display: "flex",
		flexShrink: "0",
		gap: "15px",
		maxHeight: "200px",
		overflow: "hidden",
		transition: "max-height 0.2s ease-out, padding 0.2s ease-out, opacity 0.2s ease-out",
		cursor: notification?.notificationType === "announcement" ? "pointer" : "default",
		borderWidth: notification?.notificationType === "announcement" ? "1px" : 0,
		borderColor:
			notification?.notificationType === "announcement"
				? theme.palette.primary.main
				: "transparent",
		borderStyle: "solid",
		"&.read": {
			maxHeight: 0,
			padding: 0
		}
	},
	exiting: {
		maxHeight: 0,
		padding: 0,
		margin: 0,
		opacity: 0,
		overflow: "hidden",
		transition:
			"max-height 0.22s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.22s cubic-bezier(0.4, 0, 0.2, 1), padding 0.22s cubic-bezier(0.4, 0, 0.2, 1), margin 0.22s cubic-bezier(0.4, 0, 0.2, 1)"
	},
	contentContainer: {
		flexGrow: 2,
		display: "flex",
		flexDirection: "column",
		gap: "5px"
	},
	actionIcon: {
		transition: "transform 0.15s ease-in-out",
		cursor: "pointer",
		"&:hover": {
			transform: "scale(1.10)"
		}
	},
	timeAgoLabel: {
		color: theme.palette.grey[300],
		textTransform: "lowercase"
	},
	infoIcon: {
		fill: theme.palette.primary.main,
		margin: "8px",
		width: "24px",
		height: "24px"
	},
	closeIcon: {
		fill: theme.palette.primary.main
	},
	authorAvatar: {
		width: "40px",
		height: "40px"
	},
	primaryDot: {
		width: "8px",
		height: "8px",
		borderRadius: "50%",
		backgroundColor: theme.palette.primary.main,
		flexShrink: 0
	},
	timeAgoLabelContent: {
		marginTop: "12px",
		display: "flex",
		alignItems: "center",
		gap: "5px"
	},
	titleTruncate: {
		overflow: "hidden",
		textOverflow: "ellipsis",
		display: "-webkit-box",
		WebkitLineClamp: 1,
		WebkitBoxOrient: "vertical" as const
	},
	subtitleTruncate: {
		overflow: "hidden",
		textOverflow: "ellipsis",
		display: "-webkit-box",
		WebkitLineClamp: 2,
		WebkitBoxOrient: "vertical" as const,
		color: "rgba(255,255,255,0.8)"
	}
}));

interface IPropsCustom {
	className?: string;
	onRead: (notificationId?: number) => void;
	notification: NotificationItemDto;
}

export type IProps = BoxProps & IPropsCustom;

export const NotificationDrawerItem: React.FC<IProps> = ({onRead, notification}) => {
	const {classes, cx} = useStyles(notification);
	const parser = new DOMParser();
	const doc = parser.parseFromString(notification.body ?? "", "text/html");
	const body = doc?.body?.textContent || "";
	const isAnnouncement = notification?.notificationType === "announcement";
	const [openModal, setOpenModal] = useState(false);
	const [isExiting, setIsExiting] = useState(false);
	const completeAnnouncement = useCompleteAnnouncement();
	const {
		mutate: markRead,
		isPending: isMarkingRead,
		isSuccess: markReadSuccess
	} = useMarkNotificationReadById();

	const secsSinceCreated =
		(new Date().getTime() -
			new Date(
				notification.notificationType === NotificationItemDtoNotificationType.notification
					? notification.createdAt
					: notification.publishedAt
			).getTime()) /
		1000;
	const ariaLabel = isAnnouncement
		? `Announcement: ${notification?.title ?? "Untitled"}`
		: `Notification: ${notification.subject ?? "No subject"}`;

	useEffect(() => {
		if (markReadSuccess) {
			setIsExiting(true);
		}
	}, [markReadSuccess]);

	const handleTransitionEnd = (e: React.TransitionEvent<HTMLDivElement>) => {
		if (isExiting && e.propertyName === "max-height") {
			onRead(notification.id);
		}
	};

	return (
		<>
			<Box
				className={cx(
					classes.historicNotificationItemAnnouncement,
					isExiting && classes.exiting
				)}
				onClick={() => !isExiting && setOpenModal(true)}
				onTransitionEnd={handleTransitionEnd}
				aria-label={ariaLabel}
				data-testid="historic-notification-item"
			>
				<Box>
					{isAnnouncement ? (
						<Avatar
							src={notification?.profileImage?.toString()}
							className={classes.authorAvatar}
							aria-label="Author avatar"
							data-testid="announcement-author-avatar"
						>
							{notification?.lastname?.charAt(0) || "?"}
						</Avatar>
					) : (
						<NotificationsIcon className={classes.infoIcon} />
					)}
				</Box>
				<Box className={classes.contentContainer}>
					{isAnnouncement && (
						<Box>
							<Typography
								variant="h6"
								fontWeight={700}
								className={classes.titleTruncate}
							>
								{notification?.firstname} {notification?.lastname}
							</Typography>
							<Typography variant="body2" className={classes.subtitleTruncate}>
								{notification?.jobTitle}
							</Typography>
						</Box>
					)}
					{isAnnouncement && <AnnouncementContentChips announcement={notification} />}
					<Typography
						variant={isAnnouncement ? "body1" : "h6"}
						fontWeight={700}
						className={classes.titleTruncate}
					>
						{isAnnouncement ? notification?.title : notification.subject}
					</Typography>
					<Typography variant="body1" className={classes.subtitleTruncate}>
						{isAnnouncement ? notification?.subtitle : body}
					</Typography>
					<Box className={classes.timeAgoLabelContent}>
						{(!isAnnouncement && notification.read) ||
						(isAnnouncement && notification?.complete) ? null : (
								<Box
									className={classes.primaryDot}
									data-testid="notification-read-indicator"
								/>
							)}
						<Tooltip
							placement="left"
							title={new Date(
								notification.notificationType ===
								NotificationItemDtoNotificationType.notification
									? notification.createdAt
									: notification.publishedAt
							).toLocaleString()}
						>
							<Typography className={classes.timeAgoLabel} variant="body2">
								{`${formatTimeSecondsApprox(secsSinceCreated)} ago`}
							</Typography>
						</Tooltip>
					</Box>
				</Box>
				{!isAnnouncement && (
					<Box>
						{isMarkingRead ? (
							<CircularProgress />
						) : (
							<CloseIcon
								className={cx(classes.closeIcon, classes.actionIcon)}
								onClick={() => markRead(notification.id)}
							/>
						)}
					</Box>
				)}
			</Box>
			{openModal && isAnnouncement && notification.slug && (
				<App2AnnouncementModal
					open={openModal}
					onClose={() => setOpenModal(false)}
					announcement={notification as LearnerAssignedAnnouncementDto}
					onComplete={(slug) => completeAnnouncement.mutate(slug)}
				/>
			)}
		</>
	);
};
